import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, User, Mail, Loader2 } from "lucide-react";
import api from "@/api";
import { useAuth } from "@/context/AuthContext";

export default function InviteRegister() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [inviteInfo, setInviteInfo] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/auth/invite/verify/${token}`)
      .then((r) => {
        setInviteInfo(r.data);
        setName(r.data.name || "");
      })
      .catch((err) => setError(err.response?.data?.detail || "Invito non valido"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw1 !== pw2) {
      toast.error("Le password non coincidono");
      return;
    }
    if (pw1.length < 8) {
      toast.error("La password deve essere lunga almeno 8 caratteri");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/register-invite", {
        token,
        name: name.trim(),
        password: pw1,
      });
      setUser(data.user);
      toast.success(`Benvenuto ${data.user.name}!`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore registrazione");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-md shadow-lg p-8" data-testid="invite-register">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 bg-blue-700 flex items-center justify-center rounded-md">
            <span className="font-display font-black text-white text-lg">E</span>
          </div>
          <div>
            <div className="font-display font-black text-lg">ETHOS</div>
            <div className="text-[9px] uppercase tracking-widest text-orange-500 font-semibold">Costruzioni</div>
          </div>
        </div>

        {error && (
          <div className="p-4 border border-rose-300 bg-rose-50 text-rose-900 rounded-sm text-sm">
            <b>Invito non valido</b><br/>
            {error}
          </div>
        )}

        {!error && !inviteInfo && (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Verifica invito in corso...
          </div>
        )}

        {inviteInfo && (
          <>
            <div className="overline text-blue-700 mb-2">Completa la registrazione</div>
            <h1 className="font-display text-2xl font-black mb-1">Crea il tuo account</h1>
            <p className="text-sm text-slate-600 mb-6">
              Sei stato invitato come <b className="text-slate-900">{inviteInfo.role.replace("_", " ")}</b>.
              Imposta il tuo nome e una password per iniziare.
            </p>

            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Email</label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input value={inviteInfo.email} disabled className="w-full pl-9 pr-3 py-2.5 border border-slate-300 bg-slate-100 text-slate-600 rounded-sm text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Nome e cognome</label>
                <div className="relative">
                  <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="invite-name"
                    placeholder="Mario Rossi"
                    className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-sm text-sm"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Password (minimo 8 caratteri, lettere + numeri)</label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="password"
                    value={pw1}
                    onChange={(e) => setPw1(e.target.value)}
                    data-testid="invite-pw1"
                    className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-sm text-sm"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Ripeti password</label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="password"
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    data-testid="invite-pw2"
                    className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-sm text-sm"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="btn-register-invite"
                className="w-full py-3 bg-blue-700 hover:bg-blue-800 text-white font-semibold rounded-sm disabled:opacity-60"
              >
                {submitting ? "Registrazione..." : "Crea account e accedi"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
